import React from 'react';
import PropTypes from 'prop-types';
import onClickOutside from 'react-onclickoutside';
import StylePropType from 'react-style-proptype';
import { getLocalizedText as text } from '@vp-dst/localization-util';

import ColorUtil from 'utils/ColorUtil';
import ScrollUtil from 'utils/ScrollUtil';
import { colors } from 'settings/theme';

import ProductSearchResult from 'components/ProductSearchResult';
import { defaultTextStyle } from '../settings/theme';

class ProductSearch extends React.Component {
  static scrollProductOptionIndexIntoView(index) {
    const dropdownEl = document.getElementById('product-search-results');
    const searchResultEl = document.getElementById(`product-search-result-${index}`);

    ScrollUtil.scrollElementIntoView(dropdownEl, searchResultEl);
  }

  constructor(props) {
    super(props);

    this.state = {
      focusedResultIndex: null,
      showResults: true,
    };

    this.searchResultOnMouseEnter = this.searchResultOnMouseEnter.bind(this);
    this.queryOnChange = this.queryOnChange.bind(this);
    this.queryOnFocus = this.queryOnFocus.bind(this);
    this.queryOnKeyPress = this.queryOnKeyPress.bind(this);
  }

  searchResultOnSelected(product) {
    const {
      onResultSelected,
    } = this.props;

    this.setState({
      showResults: false,
      focusedResultIndex: null,
    });

    onResultSelected(product);
  }

  searchResultOnMouseEnter(index) {
    this.setState({ focusedResultIndex: index });
  }

  handleClickOutside() {
    const {
      showResults,
    } = this.state;

    if (showResults) {
      this.setState({
        showResults: false,
      });
    }
  }

  queryOnFocus() {
    const {
      showResults,
    } = this.state;

    if (!showResults) {
      this.setState({
        showResults: true,
      });
    }
  }

  focusSearchResultIndex(index) {
    this.setState({
      focusedResultIndex: index,
    }, () => ProductSearch.scrollProductOptionIndexIntoView(index));
  }

  queryOnKeyPress(event) {
    const {
      results,
    } = this.props;

    const {
      showResults,
      focusedResultIndex,
    } = this.state;

    // only override key behavior if the results-list is shown
    if (!showResults) {
      return;
    }

    switch (event.key) {
      case 'Enter':
        if (focusedResultIndex !== null) {
          this.searchResultOnSelected(results[focusedResultIndex].product);
        }
        break;
      case 'ArrowDown':
        event.preventDefault(); // prevent cursor from moving in text box
        if (focusedResultIndex === null) {
          this.focusSearchResultIndex(0);
        } else if (focusedResultIndex < (results.length - 1)) {
          this.focusSearchResultIndex(focusedResultIndex + 1);
        }
        break;
      case 'ArrowUp':
        event.preventDefault(); // prevent cursor from moving in text box
        if (focusedResultIndex !== null && focusedResultIndex > 0) {
          this.focusSearchResultIndex(focusedResultIndex - 1);
        }
        break;
      case 'Escape':
        event.preventDefault(); // prevent esc from clearing text box
        this.setState({
          showResults: false,
        });
        break;
      default:
        break;
    }
  }

  queryOnChange(event) {
    const query = event.target.value;

    const {
      queryOnChange,
    } = this.props;

    const {
      showResults,
    } = this.state;

    if (!showResults) {
      this.setState({
        showResults: true,
      });
    }

    queryOnChange(query);
  }

  render() {
    const {
      query,
      placeholderText,
      results,
      inputRef,
      containerStyle,
      goOnClick,
      goButtonDisabled,
    } = this.props;

    const {
      focusedResultIndex,
      showResults,
    } = this.state;

    const barHeight = 50;
    const buttonWidth = 100; // localization-todo: width must fit localized text (auto doesn't seem to work)

    return (
      <div
        style={{
          position: 'relative',
          ...containerStyle,
        }}
      >
        <div
          className="input-with-button input-with-button-beside"
          style={{
            boxSizing: 'border-box',
            width: '100%',
            borderRadius: 10,

            display: 'flex',
          }}
        >
          <input
            type="search"
            ref={inputRef}
            className="stylized-input stylized-input-with-placeholder"
            style={{
              width: `calc(100% - ${buttonWidth}px)`,
              height: barHeight,
              borderRight: 'none',
              paddingRight: 15, // pulls the x (clear) button from the edge
              '-webkit-appearance': 'none',
            }}
            placeholder={placeholderText}
            value={query}
            onChange={this.queryOnChange}
            onKeyDown={this.queryOnKeyPress}
            onFocus={this.queryOnFocus}
          />
          {goButtonDisabled
            && (
              <button
                type="button"
                className="textbutton textbutton-skin-secondary"
                style={{
                  height: barHeight,
                  width: buttonWidth,
                  cursor: 'default',
                  pointerEvents: 'none',
                  boxShadow: 'none',
                  float: 'none',
                }}
              >
                <span
                  className="textbutton-icon textbutton-icon-search"
                  style={{
                    float: 'none',
                  }}
                />
              </button>
            )
          }
          {!goButtonDisabled
            && (
              <button
                type="button"
                className="textbutton textbutton-skin-promo-green"
                onClick={goOnClick}
                style={{
                  height: barHeight,
                  width: buttonWidth,
                }}
              >
                {text('go')}
              </button>
            )
          }
        </div>
        {showResults
          && (
            <div
              id="product-search-results"
              style={{
                position: 'absolute',
                top: barHeight, // move down to just below the bar
                borderRadius: 8,
                zIndex: 2,
                width: '100%',
                maxHeight: '40vh',
                overflowY: 'auto',
                overflowX: 'hidden',
                textOverflow: 'ellipsis',
                boxShadow: `0 8px 15px 0 ${ColorUtil.rgbaFromHex(colors.black, 0.2)}`,
                backgroundColor: ColorUtil.rgbaFromHex(colors.white, 0.98),
              }}
            >
              {results.length === 0 && query.length > 0
                && (
                  <div
                    style={{
                      padding: 15,
                      backgroundColor: 'rgba(255,255,255,0)',
                    }}
                  >
                    <span
                      style={{
                        ...defaultTextStyle,
                        fontSize: '1.5em',
                      }}
                    >
                      {text('sorry-there-are-no-results')}
                    </span>
                    <span
                      style={{
                        ...defaultTextStyle,
                        fontSize: '1.5em',
                        fontWeight: 'bold',
                        color: colors.blueMid,
                        paddingLeft: 5,
                      }}
                    >
                      {query}
                    </span>
                  </div>
                )
              }
              {results.map((result, index) => (
                <ProductSearchResult
                  id={`product-search-result-${index}`}
                  key={result.product.localizedName}
                  matchString={result.match.string}
                  product={result.product}
                  focused={focusedResultIndex === index}
                  onClick={() => this.searchResultOnSelected(result.product)}
                  onMouseEnter={() => this.searchResultOnMouseEnter(index)}
                />
              ))}
            </div>
          )
        }
      </div>
    );
  }
}

ProductSearch.propTypes = {
  containerStyle: StylePropType,
  onResultSelected: PropTypes.func.isRequired,
  queryOnChange: PropTypes.func.isRequired,
  goOnClick: PropTypes.func.isRequired,
  goButtonDisabled: PropTypes.bool.isRequired,
  query: PropTypes.string,
  placeholderText: PropTypes.string,
  results: PropTypes.arrayOf(PropTypes.shape({
    localizedName: PropTypes.string,
    pfid: PropTypes.string,
    localizedPriceHTML: PropTypes.string,
  })),
  inputRef: PropTypes.shape({
    current: PropTypes.object,
  }).isRequired,
};

ProductSearch.defaultProps = {
  containerStyle: {},
  query: '',
  placeholderText: '',
  results: [],
};

export default onClickOutside(ProductSearch);



// WEBPACK FOOTER //
// ./src/components/ProductSearch.js